import { CouponDetail } from '@/src/types/couponTray'
import type { PaxPricingInfo } from '@/src/types/itinerary'
import { useMemo } from 'react'

/**
 * Hook for calculating discount-related values (coupons and instant discounts)
 * Processes coupon details and pax pricing info to calculate total discounts
 */
const useDiscountCalculation = (couponDetails: CouponDetail[] = [], paxPricingInfoJson: PaxPricingInfo[] = []) => {
    // Coupon discount and code are always derived from RTK Query couponDetails
    const { totalCouponAmount, totalCouponAmountWithVat, couponCode } = useMemo(() => {
        let totalCouponAmount = 0
        let totalCouponAmountWithVat = 0
        couponDetails.forEach((couponDetail: CouponDetail) => {
            const { walletPromo = false, amount = 0, ct_vat } = couponDetail || {}
            if (!walletPromo && amount && !isNaN(amount)) {
                totalCouponAmount += amount
                totalCouponAmountWithVat = totalCouponAmountWithVat + amount + (ct_vat || 0)
            }
        })
        return {
            totalCouponAmount,
            totalCouponAmountWithVat,
            couponCode: couponDetails[0]?.couponCode || ''
        }
    }, [couponDetails])

    // Calculate total instant discount across all pax
    const totalInstantDiscount = useMemo(() => {
        if (!Array.isArray(paxPricingInfoJson)) return 0

        return paxPricingInfoJson.reduce((acc, pax) => {
            const pricingElements = pax.pricingInfoJson?.[0]?.pricingElementJson || []
            const paxDiscount = pricingElements
                .filter(e => e.category === 'DIS')
                .reduce((sum, e) => sum + (e.amount || 0), 0)
            return acc + paxDiscount
        }, 0)
    }, [paxPricingInfoJson])

    return {
        totalCouponAmount,
        totalCouponAmountWithVat,
        couponCode,
        totalInstantDiscount
    }
}

export default useDiscountCalculation
